/**
 * destination-region.js — row 1, right: "how does this ship".
 *
 * Agent ✨ · Share ▾ · Publish (split) · account avatar.
 *
 * ── The headline change: Publish stops being item six ─────────────────────
 * `Export` was the label for nine unrelated things. The primary button said
 * *Export*; behind it sat Download .json, Download .zip, Save to cloud, Review changes,
 * **Publish site**, Save Preview, Save as template, Export HTML and
 * Export 11ty. The most important action in the product was the sixth item in a
 * menu named after something else.
 *
 * `#pb-export-btn` therefore BECOMES the Publish primary — the census records
 * exactly that destination for it — and grows a split arrow for the menu. The
 * id stays with the control: `shell.js` reads its `aria-busy` on Ctrl+S,
 * `tour-registry.js` walks the user to it, and `styles/toolbar.css` paints its
 * ambient progress track. `#pb-publish-site-btn` (the old menu row) folds into
 * the primary, which carries the `publish-site` action attribute so the parity
 * gate can still see it.
 *
 * **The menu behind the arrow is NOT restructured here.** Its nine rows keep
 * their ids, their actions, their order and their group labels; only their
 * construction moves from an `innerHTML` blob to `createButton`. CMD-4 owns the
 * publish branch table, the destination card and the project/publish split —
 * and it cannot retire a single row until the CMD-0 parity matrix is checked
 * off. Rebuilding the contents in the same phase that rehomes the control would
 * make "did the move break it" and "did the redesign break it" the same
 * question.
 *
 * ── Share is a new control, made of existing actions ──────────────────────
 * The access surface, Comments and Comment Mode were spread across the project
 * chip menu and the ⋮ junk drawer. They declare `share-menu` as their home in
 * the parity matrix, so they gather here. The comment ids move with them,
 * which is what keeps `shell.js`'s `syncCommentMenuItems` — which finds them
 * by id and rewrites their labels — working with no change at all. Those rows
 * carry no icon because the shell writes `textContent`, which would silently
 * delete a glyph child.
 *
 * ── The agent glyph loses its private copy, not its gradient ──────────────
 * The button drew a two-sparkle path with a gradient fill and an inline
 * `<linearGradient>`. That exact outline is already `icon('skin')`, so the
 * drawing comes from the registry now and the gradient is applied in CSS
 * against a defs block emitted once. `#pb-agent-trigger-grad` survives as the
 * paint-server id it always was.
 */

import { SAVE_LABELS, PUBLISH_LABELS } from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { reportError } from '../../core/report-error.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { ToolbarMenu } from '../toolbar-menu.js';
import { getProfile } from '../../core/profile-store.js';
import { accountLabel, renderAvatarInto } from '../account-avatar.js';
import { describePublishDestination } from './publish-branch.js';
import { isAgentReachable } from '../agent-chat/agent-origin.js';
import {
  beginOperation,
  endOperation,
  isOperationActive,
  subscribeOperations
} from './command-operation-state.js';
import { shareActionById } from './share-actions.js';

function isForcedAuthUiForDev() {
  return typeof window !== 'undefined' && window.__PB_FORCE_AUTH_UI === true;
}

/** How long a destination read stays trusted before the menu re-reads it. */
const DESTINATION_STALE_MS = 60_000;

export class DestinationRegion {
  /**
   * @param {HTMLElement} container — the `row1.destination` slot
   * @param {{initialAuthPayload?: object|null}} [opts] — the boot auth payload.
   *   INJECTED rather than imported: `getCurrentUser()` lives in `shell.js`, and
   *   a command-bar module that imports the shell is unloadable under node.
   *   The gate resolves auth BEFORE this exists, so its `auth:state` emission
   *   has no listener yet — without the seed the chip stays hidden until the
   *   next auth event.
   */
  constructor(container, { initialAuthPayload = null } = {}) {
    this._container = container;
    this._offs = [];
    this._authPayload = initialAuthPayload;
    this._operationTokens = { save: null, export: null };
    this._exportCancelable = false;
    this._exportProgress = 0;
    this._render();
    this._listen();
    this._syncAuth(this._authPayload);
  }

  destroy() {
    clearTimeout(this._liveTimer);
    this._liveTimer = null;
    this._destinationRead?.abort();
    this._destinationRead = null;
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    this._shareMenuCtrl?.destroy();
    this._publishMenuCtrl?.destroy();
    this._shareMenuCtrl = null;
    this._publishMenuCtrl = null;
    // Component teardown is not an operation outcome. A button-created token
    // is handed to shell.js immediately; ending it here would make a remounted
    // bar accept a second publish while the first upload is still running.
    this._operationTokens = { save: null, export: null };
  }

  _render() {
    this._container.classList.add('pb-cb-destination');

    this._container.append(
      ...[
        this._renderAgentGradientDefs(),
        this._renderAgent(),
        this._renderShare(),
        this._renderPublish(),
        this._renderAccountChip()
      ].filter(Boolean)
    );
  }

  /**
   * A paint server, not a drawing. Referenced from CSS as
   * `fill: url(#pb-agent-trigger-grad)`, which is how the brand gradient
   * survives the glyph moving into a registry that only emits `currentColor`.
   */
  _renderAgentGradientDefs() {
    // Built as DOM nodes rather than as an SVG markup literal, deliberately.
    // `ui/icons.js` is the only file allowed to hold glyph markup — two sweep
    // rules say so (`no-inline-svg-literal`, `no-sized-svg-attr`) — and the
    // first draft of this method tripped both. Annotating past them would have
    // been the wrong answer twice over: this is not a glyph, and it does not
    // need a size attribute. `.pb-cb-defs` collapses the holder in CSS.
    const NS = 'http://www.w3.org/2000/svg';
    const holder = document.createElement('div');
    holder.className = 'pb-cb-defs';
    holder.setAttribute('aria-hidden', 'true');

    const root = document.createElementNS(NS, 'svg');
    root.setAttribute('focusable', 'false');
    const defs = document.createElementNS(NS, 'defs');
    const grad = document.createElementNS(NS, 'linearGradient');
    // A LITERAL id: `#pb-agent-trigger-grad` is an authored id the census
    // tracks, and a template hole is not authorship.
    grad.id = 'pb-agent-trigger-grad';
    grad.setAttribute('x1', '4');
    grad.setAttribute('y1', '4');
    grad.setAttribute('x2', '22');
    grad.setAttribute('y2', '21');
    grad.setAttribute('gradientUnits', 'userSpaceOnUse');
    for (const [offset, color] of [
      ['0', '#0078d4'],
      ['0.55', '#0EA5E9'],
      ['1', '#8B5CF6']
    ]) {
      const stop = document.createElementNS(NS, 'stop');
      stop.setAttribute('offset', offset);
      stop.setAttribute('stop-color', color);
      grad.appendChild(stop);
    }
    defs.appendChild(grad);
    root.appendChild(defs);
    holder.appendChild(root);
    return holder;
  }

  /**
   * Absent, not disabled, when the proxy cannot be reached from this origin.
   * A disabled control says "not now"; this one is "not here" — the agent is a
   * localhost developer tool and a deployed page has no route to it at all.
   */
  _renderAgent() {
    if (!isAgentReachable()) {
      this._agentBtn = null;
      return null;
    }
    this._agentBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-agent-trigger',
      id: 'pb-agent-btn',
      icon: icon('skin'),
      ariaLabel: 'Open PageStitch Agent',
      tooltip: 'PageStitch Agent',
      tooltipShortcut: 'Ctrl+J',
      onClick: () => eventBus.emit('agent:toggle')
    });
    this._agentBtn.setAttribute('aria-pressed', 'false');
    return this._agentBtn;
  }

  _renderShare() {
    const wrap = document.createElement('div');
    wrap.className = 'pb-cb-menu-wrap';

    this._shareBtn = createButton({
      variant: 'ghost',
      className: 'pb-cb-share',
      icon: icon('share', { size: 'sm' }),
      label: PUBLISH_LABELS.share,
      // A span, not a bare text node: the label is hidden below 1023px and a
      // text node cannot be targeted by CSS. `ariaLabel` carries the name.
      labelClass: 'pb-btn-label',
      ariaLabel: PUBLISH_LABELS.share,
      tooltip: 'Share and collaborate',
      ariaHaspopup: 'true',
      ariaExpanded: 'false'
    });

    this._shareMenu = document.createElement('div');
    this._shareMenu.className = 'pb-cb-menu';
    this._shareMenu.id = 'pb-share-menu';
    this._shareMenu.setAttribute('role', 'menu');
    // FN-8: every way of letting someone else see this project now lives here,
    // and each row says what the RECIPIENT can do. Labels/hints/events come
    // from `share-actions.js` so the phone sheet renders the same five; the
    // `data-*-action` literals stay authored HERE because command-bar-census.js
    // harvests them from this file to build the action-parity matrix.
    const act = (id) => shareActionById(id);
    this._shareMenu.append(
      this._menuItem({
        id: 'pb-share-project-btn',
        'data-project-action': 'share',
        label: act('invite').label,
        hint: act('invite').hint,
        glyph: act('invite').glyph
      }),
      this._menuItem({
        id: 'pb-review-link-btn',
        'data-project-action': 'review-link',
        label: act('review-link').label,
        hint: act('review-link').hint,
        glyph: act('review-link').glyph
      }),
      // Moved out of the Publish menu's "Share a draft" group: a preview link
      // is a way of showing someone, not a way of publishing.
      this._menuItem({
        id: 'pb-save-preview-btn',
        'data-save-action': 'save-preview',
        label: act('save-preview').label,
        hint: act('save-preview').hint,
        glyph: act('save-preview').glyph
      }),
      // Moved out of the account menu, where saved preview links were filed
      // under the person rather than under the project they belong to.
      this._menuItem({
        id: 'pb-my-previews-btn',
        'data-project-action': 'my-previews',
        label: act('my-previews').label,
        hint: act('my-previews').hint,
        glyph: act('my-previews').glyph
      }),
      // NO glyph on purpose — `shell.js` rewrites this row's label to carry the
      // unseen count, and an icon child would not survive that rewrite.
      //
      // ONE comment row, not two. "Comment Mode" used to sit beside this and
      // open the same panel as a side effect, so the menu offered two doors
      // onto overlapping behaviour and the unseen count was stranded on the
      // door nobody opened. Arming the mode is now the panel's own primary
      // action, next to the threads it produces.
      this._menuItem({
        id: 'pb-comments-menu-btn',
        'data-overflow-action': 'comments',
        label: act('comments').label,
        hint: act('comments').hint,
        hidden: true
      })
    );

    wrap.append(this._shareBtn, this._shareMenu);
    return wrap;
  }

  _renderPublish() {
    const wrap = document.createElement('div');
    wrap.className = 'pb-cb-menu-wrap pb-cb-split';

    this._publishLabel = document.createElement('span');
    this._publishLabel.id = 'pb-export-btn-label';
    this._publishLabel.textContent = PUBLISH_LABELS.publish;

    this._publishBtn = createButton({
      variant: 'primary',
      className: 'pb-cb-publish',
      id: 'pb-export-btn',
      icon: icon('globe', { size: 'sm' }),
      content: this._publishLabel,
      ariaLabel: PUBLISH_LABELS.publish,
      tooltip: PUBLISH_LABELS.site
    });
    // Keeps the action visible to the parity gate now that the old
    // `publish-site` menu row is folded into this button.
    this._publishBtn.setAttribute('data-save-action', 'publish-site');

    this._publishArrow = createButton({
      variant: 'primary',
      className: 'pb-cb-publish-arrow',
      id: 'pb-publish-menu-btn',
      icon: icon('chevronDown', { size: 'xs' }),
      ariaLabel: 'Publish and export options',
      ariaHaspopup: 'true',
      ariaExpanded: 'false'
    });

    this._publishMenu = this._buildPublishMenu();
    wrap.append(this._publishBtn, this._publishArrow, this._publishMenu);
    return wrap;
  }

  /**
   * Today's Export menu, rebuilt through the kit with every id, action, order
   * and group label preserved. CMD-4 restructures the CONTENTS; this phase only
   * changes who owns them.
   */
  _buildPublishMenu() {
    const menu = document.createElement('div');
    menu.className = 'pb-cb-menu';
    menu.id = 'pb-export-menu';
    menu.setAttribute('role', 'menu');

    // The destination card is the first thing in the menu, because "where does
    // this go and is it current" is the question the arrow is opened to answer.
    this._destinationCard = document.createElement('section');
    this._destinationCard.className = 'pb-cb-destination-card';
    menu.append(this._destinationCard, this._divider());
    this._renderDestinationCard();

    this._cloudGroup = document.createElement('div');
    this._cloudGroup.className = 'pb-cb-menu-group';
    this._cloudGroup.id = 'pb-review-changes-group';
    this._cloudGroup.setAttribute('role', 'presentation');
    this._cloudGroup.hidden = true;
    this._cloudGroup.append(
      this._menuItem({
        id: 'pb-review-changes-btn',
        'data-save-action': 'review-changes',
        label: SAVE_LABELS.review
      }),
      this._divider()
    );
    menu.appendChild(this._cloudGroup);

    menu.append(
      // FN-8: the "Share a draft" group moved to the Share menu whole. This
      // menu is now only about the destination — publish, its address, and the
      // code you can take away.
      this._groupLabel('Download the code'),
      this._menuItem({ 'data-export-action': 'html', label: PUBLISH_LABELS.html, glyph: 'html' }),
      this._menuItem({
        'data-export-action': '11ty',
        label: PUBLISH_LABELS.eleventy,
        glyph: 'eleventy'
      }),
      // Written LITERALLY, like its two siblings: the census parity gate reads
      // `data-*-action` keys out of this source as literals, so an assembled
      // key would be invisible to it.
      this._menuItem({
        'data-export-action': 'astro',
        label: PUBLISH_LABELS.astro,
        // Each generator wears its OWN mark. It used to wear `code`, which is
        // byte-identical to the `html` glyph one row up, so the menu drew the
        // same icon for "flat files" and "an Astro repo".
        glyph: 'astro'
      })
    );

    return menu;
  }

  /**
   * The destination card — "where does this go, and is it current".
   *
   * Its copy is a STATE, never a count. `selectPublishBaseline()` resolves a
   * version ROW; it does not diff, and there is no diff-summary service in this
   * repo. The prototype's "7 changes" is illustrative, and a fabricated number
   * beside a Publish button is the kind of detail a user trusts *because* it
   * looks computed.
   *
   * The values arrive through `setPublishDestination()`, which the shell calls
   * after a site read. Until then the card says "Not published yet" — the same
   * thing an unpublished project says, and the honest default: the card must
   * never claim "Up to date" from missing data, because that is the one wrong
   * answer that tells someone there is nothing left to ship.
   */
  _renderDestinationCard() {
    const card = describePublishDestination(this._destination || {});
    this._destinationCard.dataset.state = card.state;
    this._destinationCard.setAttribute('aria-busy', String(card.state === 'loading'));
    this._destinationCard.replaceChildren();

    const label = document.createElement('p');
    label.className = 'pb-cb-destination-address';
    label.textContent = card.label;
    this._destinationCard.appendChild(label);

    if (card.detail) {
      const detail = document.createElement('p');
      detail.className = 'pb-cb-destination-detail';
      detail.textContent = card.detail;
      this._destinationCard.appendChild(detail);
    }

    // Nothing to route to until the read settles; a failed read offers the
    // read again rather than guessing which address action applies.
    if (card.state === 'loading') return;
    if (card.state === 'unreadable') {
      this._destinationCard.appendChild(
        createButton({
          variant: 'bare',
          className: 'pb-cb-menu-item',
          role: 'menuitem',
          label: 'Check again',
          onClick: () => this.refreshDestination({ force: true })
        })
      );
      return;
    }

    // Changing the address and unpublishing both live in the modal, which owns
    // slug availability and claimed-site management. The card is the route to
    // it, not a second implementation of it.
    this._destinationCard.appendChild(
      createButton({
        variant: 'bare',
        className: 'pb-cb-menu-item',
        role: 'menuitem',
        label:
          card.state === 'unpublished'
            ? PUBLISH_LABELS.chooseAddress
            : PUBLISH_LABELS.manageAddress,
        onClick: () => {
          this._publishMenuCtrl?.close();
          eventBus.emit('toolbar:publish-manage');
        }
      })
    );
  }

  /**
   * Called by the shell after a site read. `undefined` for `site` means the
   * read has not happened or failed, and is deliberately NOT the same as null.
   */
  setPublishDestination(destination) {
    this._destination = destination
      ? { fetchedAt: Date.now(), projectId: this._currentProjectId(), ...destination }
      : null;
    if (this._destinationCard) this._renderDestinationCard();
  }

  /**
   * BC-3. The card follows the PROJECT, not the publish flow. The shell injects
   * how to read a destination — this region stays store-blind and importable
   * under node — and the region decides when: on menu open (if the cached read
   * is for another project, failed, or older than a minute), on a project
   * change, and after a publish.
   *
   * @param {{getProjectId: () => string|null, read: (projectId: string, opts: {signal: AbortSignal}) => Promise<{site: object|null, cloudVersion: number|null, publishedVersion: number|null}>}} source
   */
  setDestinationSource(source) {
    this._destinationSource = source || null;
  }

  _currentProjectId() {
    return this._destinationSource?.getProjectId?.() ?? null;
  }

  /** The project changed under the card: forget what it said, read again. */
  resetDestination() {
    this._destinationRead?.abort();
    this._destinationRead = null;
    this._destination = { status: 'loading', projectId: this._currentProjectId() };
    if (this._destinationCard) this._renderDestinationCard();
    if (this._publishMenuCtrl?.isOpen) void this.refreshDestination({ force: true });
  }

  async refreshDestination({ force = false } = {}) {
    const source = this._destinationSource;
    if (!source) return;
    const projectId = this._currentProjectId();
    const cached = this._destination;
    const fresh =
      cached &&
      cached.projectId === projectId &&
      cached.status !== 'loading' &&
      cached.status !== 'error' &&
      Date.now() - (cached.fetchedAt || 0) < DESTINATION_STALE_MS;
    if (fresh && !force) return;
    // One read per project at a time: a second open while the first read is
    // still out waits for it rather than cancelling and repeating it.
    if (this._destinationRead && this._destinationReadProjectId === projectId) {
      return this._destinationReadPromise;
    }

    this._destinationRead?.abort();
    const controller = new AbortController();
    this._destinationRead = controller;
    this._destinationReadProjectId = projectId;
    let settleRead;
    this._destinationReadPromise = new Promise((resolve) => (settleRead = resolve));
    if (!cached || cached.projectId !== projectId || cached.status === 'error') {
      this._destination = { status: 'loading', projectId };
      if (this._destinationCard) this._renderDestinationCard();
    }
    try {
      const result = await source.read(projectId, { signal: controller.signal });
      if (controller.signal.aborted || this._currentProjectId() !== projectId) return;
      this._destination = { ...result, projectId, fetchedAt: Date.now(), status: null };
    } catch (err) {
      if (controller.signal.aborted || err?.name === 'AbortError') return;
      if (this._currentProjectId() !== projectId) return;
      // Expected flow: an unreadable site is a STATE on the card ("Could not
      // check publish status · Check again"), not a toast on menu open.
      this._destination = { status: 'error', projectId, fetchedAt: Date.now() };
    } finally {
      if (this._destinationRead === controller) {
        this._destinationRead = null;
        this._destinationReadProjectId = null;
      }
      settleRead();
    }
    if (this._destinationCard) this._renderDestinationCard();
  }

  _renderAccountChip() {
    this._authAvatar = document.createElement('span');
    this._authAvatar.className = 'pb-auth-chip-avatar';
    this._authAvatar.id = 'pb-auth-chip-avatar';
    this._authAvatar.setAttribute('aria-hidden', 'true');

    this._authLabel = document.createElement('span');
    this._authLabel.className = 'pb-auth-chip-label';
    this._authLabel.id = 'pb-auth-chip-label';

    this._authChip = createButton({
      variant: 'bare',
      className: 'pb-auth-chip',
      id: 'pb-auth-chip',
      content: [this._authAvatar, this._authLabel],
      ariaLabel: 'Account',
      tooltip: 'Account',
      ariaHaspopup: 'menu',
      // A hand-written in-flight guard rather than the kit's `onAsyncClick`,
      // and the reason is specific: `setButtonBusy` saves and RESTORES the
      // button's `innerHTML`, which replaces the avatar and label with fresh
      // nodes. `this._authAvatar` and `this._authLabel` would still point at
      // the originals, so every later `_syncAuth` would write into detached
      // elements and the chip would silently stop updating. The guard is the
      // part worth keeping; the DOM rewrite is not.
      onClick: () => this._openAccountPanel()
    });
    this._authChip.hidden = true;
    return this._authChip;
  }

  _openAccountPanel() {
    if (this._accountPanelPending) return;
    this._accountPanelPending = true;
    import('../account-panel.js')
      .then(({ toggleAccountPanel }) =>
        toggleAccountPanel(this._authChip, { authPayload: this._authPayload })
      )
      .catch((err) =>
        reportError('command-bar', err, {
          user: 'toast',
          message: 'Could not open the account menu.'
        })
      )
      .finally(() => {
        this._accountPanelPending = false;
      });
  }

  // ── menu building blocks ──────────────────────────────────────────────────

  _divider() {
    const el = document.createElement('div');
    el.className = 'pb-cb-menu-divider';
    return el;
  }

  _groupLabel(text) {
    const el = document.createElement('div');
    el.className = 'pb-cb-menu-group-label';
    el.setAttribute('role', 'presentation');
    el.textContent = text;
    return el;
  }

  /**
   * The action is declared as a LITERAL `data-*-action` key beside its value,
   * never as `dataset[kind + 'Action']`. The census parity gate reads these out
   * of the source, and an action assembled from fragments is an action that
   * silently stops having a declared destination — which is exactly what
   * happened on this file's first draft, and what the gate caught.
   */
  _menuItem({ id, label, hint, glyph, hidden = false, ...attrs }) {
    const btn = createButton({
      variant: 'bare',
      className: 'pb-cb-menu-item',
      id,
      role: 'menuitem',
      icon: glyph ? icon(glyph, { size: 'sm' }) : undefined,
      label,
      labelClass: 'pb-cb-menu-item-label'
    });
    if (hint) {
      const copy = document.createElement('span');
      copy.className = 'pb-cb-menu-item-copy';
      copy.appendChild(btn.querySelector('.pb-cb-menu-item-label'));
      const detail = document.createElement('span');
      detail.className = 'pb-cb-menu-item-hint';
      detail.textContent = hint;
      copy.appendChild(detail);
      btn.appendChild(copy);
    }
    for (const [name, value] of Object.entries(attrs)) btn.setAttribute(name, value);
    btn.hidden = hidden;
    return btn;
  }

  // ── wiring ────────────────────────────────────────────────────────────────

  _listen() {
    const on = (name, fn) => this._offs.push(eventBus.on(name, fn));
    this._offs.push(
      subscribeOperations(({ id }) => {
        if (id === 'save' || id === 'export') this._syncBusyChrome();
      })
    );

    this._shareMenuCtrl = new ToolbarMenu(this._shareBtn, this._shareMenu, {
      align: 'right',
      width: 230,
      onOpen: () => eventBus.emit('toolbar:menu-opened')
    });
    this._publishMenuCtrl = new ToolbarMenu(this._publishArrow, this._publishMenu, {
      align: 'right',
      width: 250,
      // Anchor under the whole split control so the menu lines up with the
      // primary, not with the 28px arrow.
      getAnchorRect: () => this._publishBtn.parentElement.getBoundingClientRect(),
      onOpen: () => {
        eventBus.emit('toolbar:menu-opened');
        // The shell answers by toggling per-tab item state (the review item is
        // disabled in read-only tabs) — this region stays store-blind.
        eventBus.emit('toolbar:export-menu-open', { menu: this._publishMenu });
        void this.refreshDestination();
      }
    });

    this._shareMenu.addEventListener('click', (e) => {
      // The save-preview row moved here from the publish menu, and its guard
      // moved WITH it: the publish menu's listener refused while a save or
      // export was in flight, and dropping that on the way across would have
      // let a second click mint a second preview version.
      if (isOperationActive('save') || isOperationActive('export')) return;
      const saveAction = e.target.closest('[data-save-action]')?.dataset.saveAction;
      if (saveAction === 'save-preview') return void eventBus.emit('toolbar:save-preview');

      const btn = e.target.closest('[data-project-action], [data-overflow-action]');
      const action = btn?.dataset.projectAction || btn?.dataset.overflowAction;
      if (!action) return;
      // `access` is an EXPLICIT override for this opening only — it does not
      // rewrite the remembered access mode, which the plain Share entry still
      // honours. Someone who asks for a review link is telling you about this
      // share, not changing their default.
      if (action === 'share') eventBus.emit('toolbar:share-project', { access: 'edit' });
      else if (action === 'review-link')
        eventBus.emit('toolbar:share-project', { access: 'review' });
      else if (action === 'my-previews') eventBus.emit('toolbar:my-previews');
      else if (action === 'comments') eventBus.emit('toolbar:open-comments');
    });

    // P9 ambient export progress: while a cancelable export runs, the control
    // under the pointer IS the cancel target — no canvas-covering progress
    // dialog. CAPTURE phase with `stopImmediatePropagation`, so the cancel wins
    // over the publish handler registered below rather than racing it.
    this._publishBtn.addEventListener(
      'click',
      (event) => {
        if (!isOperationActive('export') || !this._exportCancelable) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        eventBus.emit('toolbar:cancel-export');
      },
      true
    );

    // Publish primary. The busy state is set here for the same reason the old
    // menu row set it: the flow runs a compatibility gate and an audit gate
    // before any bytes exist, and both used to run with the control looking
    // idle. `shell.js` clears it on every exit route, cancellation included.
    this._publishBtn.addEventListener('click', () => {
      if (isOperationActive('save') || isOperationActive('export')) return;
      const operationToken = this._setExportBusy(true, 'Preparing…');
      if (operationToken !== null) eventBus.emit('toolbar:publish-site', { operationToken });
    });

    this._publishMenu.addEventListener('click', (e) => {
      if (isOperationActive('save') || isOperationActive('export')) return;
      const saveAction = e.target.closest('[data-save-action]')?.dataset.saveAction;
      if (saveAction === 'review-changes') return void eventBus.emit('toolbar:review-changes');

      const exportAction = e.target.closest('[data-export-action]')?.dataset.exportAction;
      if (exportAction === 'html') {
        const operationToken = this._setExportBusy(true, 'Preparing…');
        if (operationToken !== null) eventBus.emit('toolbar:export', { operationToken });
      } else if (exportAction === '11ty') {
        // Symmetry with the html branch: the 11ty path shows a settings modal
        // and an audit gate before any bytes are produced.
        const operationToken = this._setExportBusy(true, 'Preparing…');
        if (operationToken !== null) eventBus.emit('toolbar:export-11ty', { operationToken });
      } else if (exportAction === 'astro') {
        const operationToken = this._setExportBusy(true, 'Preparing…');
        if (operationToken !== null) eventBus.emit('toolbar:export-astro', { operationToken });
      }
    });

    // Contracts §9: `✓ Live` is driven by the publish RESULT, never by a timer.
    // The prototype's fixed 1.4s is a prototype artifact — and a success tick
    // on a timer is a lie the moment a publish takes 1.5 seconds or fails.
    on('publish:result', ({ ok } = {}) => {
      clearTimeout(this._liveTimer);
      // A publish (or a failed one) is exactly when the card's facts move.
      void this.refreshDestination({ force: true });
      if (!ok) {
        this._published = false;
        this._syncBusyChrome();
        return;
      }
      this._published = true;
      this._syncBusyChrome();
      // The tick is a confirmation, not a state: it reverts to "Publish" so the
      // button never reads as though it were still mid-publish.
      this._liveTimer = setTimeout(() => {
        this._published = false;
        this._syncBusyChrome();
      }, 2400);
    });

    on('toolbar:operation-state', ({ type, busy, label, percent, cancelable }) => {
      if (type === 'save') this._setSaveBusy(Boolean(busy), label);
      if (type === 'export') this._setExportBusy(Boolean(busy), label, { percent, cancelable });
    });

    // Optional throughout: an unreachable proxy renders no trigger, and these
    // events still arrive from any other tab-level source.
    on('agent:state-changed', ({ open }) => {
      this._agentBtn?.setAttribute('aria-pressed', String(Boolean(open)));
      this._agentBtn?.setAttribute('aria-label', `${open ? 'Close' : 'Open'} PageStitch Agent`);
      this._agentBtn?.classList.toggle('pb-active', Boolean(open));
      // Opening the panel is how the badge is acknowledged.
      if (open) this._agentBtn?.classList.remove('pb-agent-attention', 'pb-agent-attention-error');
    });
    // A run that settled behind a closed panel gets a dot so it is not missed.
    on('agent:run-finished', ({ open, outcome }) => {
      if (open) return;
      this._agentBtn?.classList.add('pb-agent-attention');
      this._agentBtn?.classList.toggle('pb-agent-attention-error', outcome === 'error');
    });

    on('auth:state', (payload) => {
      this._authPayload = payload;
      this._syncAuth(payload);
    });
    // The phone More sheet emits the same route as this desktop chip. Keeping
    // construction here means account-panel still has one owner and one
    // in-flight guard across both affordances.
    on('toolbar:open-account', () => this._openAccountPanel());
    // Profile edits (display name / avatar) repaint the chip in place.
    on('profile:changed', () => this._syncAuth(this._authPayload));
  }

  // ── auth ──────────────────────────────────────────────────────────────────

  _syncAuth(payload) {
    const isRealAccount = Boolean(
      payload && payload.sub && payload.authMode !== 'single-user' && payload.sub !== 'admin'
    );
    // Dev-only local-proxy affordance: render the chip while keeping cloud
    // project endpoints hidden for the single-user fallback.
    const showAccountUi = isRealAccount || (isForcedAuthUiForDev() && Boolean(payload?.sub));
    this._cloudGroup.hidden = !isRealAccount;

    if (!showAccountUi) {
      this._authChip.hidden = true;
      return;
    }
    // Prefer the account profile (display name / avatar image) when loaded;
    // degrade to email/sub initials, which is profile-store's documented
    // fallback.
    const profile = getProfile();
    const display = accountLabel(profile, payload);
    this._authLabel.textContent = display.length > 24 ? `${display.slice(0, 23)}…` : display;
    try {
      renderAvatarInto(this._authAvatar, { profile, authPayload: payload });
    } catch (err) {
      reportError('command-bar', err, { user: 'silent' });
    }
    this._authChip.hidden = false;
  }

  // ── busy chrome ───────────────────────────────────────────────────────────

  _setSaveBusy(busy, label) {
    const token = this._setOperationBusy('save', busy, label || 'Saving…');
    this._saveBusyLabel = busy ? label || 'Saving…' : null;
    this._syncBusyChrome();
    if (busy) this._publishMenuCtrl?.close();
    return token;
  }

  _setExportBusy(busy, label, { percent = null, cancelable = false } = {}) {
    const token = this._setOperationBusy('export', busy, label || 'Exporting…');
    this._exportBusyLabel = busy ? label || 'Exporting…' : null;
    this._exportCancelable = busy && Boolean(cancelable);
    this._exportProgress =
      busy && Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
    this._syncBusyChrome();
    if (busy) this._publishMenuCtrl?.close();
    return token;
  }

  _syncBusyChrome() {
    const saveBusy = isOperationActive('save');
    const exportBusy = isOperationActive('export');
    const busy = saveBusy || exportBusy;
    const label = saveBusy
      ? this._saveBusyLabel || 'Saving…'
      : this._exportBusyLabel || 'Exporting…';
    const busyLabel = this._exportCancelable ? `${label} — click to cancel` : label;

    this._publishBtn.setAttribute('aria-disabled', String(busy && !this._exportCancelable));
    this._publishBtn.setAttribute('aria-busy', String(busy));
    this._publishBtn.classList.toggle('pb-btn-loading', saveBusy);
    this._publishBtn.classList.toggle('pb-export-tracking', exportBusy);
    this._publishBtn.style.setProperty('--pb-export-progress', `${this._exportProgress}%`);
    this._publishBtn.dataset.tooltip = busy ? busyLabel : PUBLISH_LABELS.site;
    this._publishBtn.setAttribute('aria-label', busy ? busyLabel : PUBLISH_LABELS.publish);
    this._publishLabel.textContent = busy
      ? `${label}${this._exportCancelable ? '  ×' : ''}`
      : this._published
        ? '✓ Live'
        : PUBLISH_LABELS.publish;

    // Contracts §5: busy disables BOTH halves. The old bar guarded only the
    // primary, so the arrow stayed live and could open a menu of actions that
    // were all already disabled behind it.
    this._publishArrow.disabled = busy;
    this._publishArrow.setAttribute('aria-disabled', String(busy));

    for (const item of this._publishMenu.querySelectorAll(
      '[data-save-action], [data-export-action]'
    )) {
      item.setAttribute('aria-disabled', String(busy));
    }
  }

  /** Adapt the shell's existing busy event onto the one shared operation model. */
  _setOperationBusy(id, busy, label) {
    const held = this._operationTokens[id];
    if (busy) {
      if (!isOperationActive(id)) {
        this._operationTokens[id] = beginOperation(id, { label });
      }
      return this._operationTokens[id];
    }
    if (held !== null) endOperation(held);
    this._operationTokens[id] = null;
    return null;
  }

  /** Cancel-while-busy: the tracking control IS the cancel target (P9). */
  get publishButton() {
    return this._publishBtn;
  }
}
